import pino from 'pino';
import type { Logger } from 'pino';
import { safeStringify } from './safe-json';

const isEdgeRuntime = typeof (globalThis as any).EdgeRuntime === 'string';

const levelValue = (l: string) =>
  ({ trace: 10, debug: 20, info: 30, warn: 40, error: 50, fatal: 60 })[l] || 30;

/**
 * Edge Runtime 兼容的轻量 Logger
 * Pino 核心在 Edge 中不完全可用（依赖 fs / worker_threads），
 * 因此在 Edge 环境回退到基于 console 的 JSON 输出。
 */
function createEdgeLogger(options: {
  level?: string;
  base?: Record<string, unknown>;
}): Logger {
  const base = options.base || {};
  const minLevel = levelValue(options.level || 'info');

  const logger = {
    child: (bindings: Record<string, unknown>) =>
      createEdgeLogger({ ...options, base: { ...base, ...bindings } }),
  } as any;

  (['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const).forEach(
    (name) => {
      const val = levelValue(name);
      logger[name] = (...args: any[]) => {
        if (val < minLevel) return;
        const [a0, a1] = args;
        const obj = typeof a0 === 'string' ? {} : a0;
        const msg =
          typeof a1 === 'string' ? a1 : typeof a0 === 'string' ? a0 : '';
        const output = { level: val, ...base, ...obj, msg };
        if (name === 'error' || name === 'fatal')
          console.error(safeStringify(output));
        else if (name === 'warn') console.warn(safeStringify(output));
        else console.log(safeStringify(output));
      };
    }
  );

  return logger as Logger;
}

const loggerConfig = {
  level: process.env.LOG_LEVEL || 'debug',
  base: {
    service: process.env.OTEL_SERVICE_NAME || 'bkgs-frontend-console',
    env: process.env.NODE_ENV,
  },
};

/**
 * 基础 Pino Logger
 * - 生产环境（Node.js）输出结构化单行 JSON（SLS 按行采集时一条日志一条记录，不拆行）
 * - 开发环境（Node.js）使用 pino-pretty 美化输出，并落盘 logs/app.log
 * - Edge Runtime 中使用兼容层，避免 transport / fs 不可用导致的崩溃
 *
 * 注意：本模块会被 Middleware / Edge / Client 一并打包，模块作用域禁止出现
 * eval / process.cwd / node:fs 等 Node 专属 API（Turbopack 对 Edge 的静态检查
 * 会直接报错），Node 专属能力只能出现在运行时分支内。
 */
export const baseLogger: Logger = isEdgeRuntime
  ? createEdgeLogger(loggerConfig)
  : process.env.NODE_ENV === 'development'
    ? pino({
      ...loggerConfig,
      transport: {
        targets: [
          // 控制台：可读格式
          {
            target: 'pino-pretty',
            level: loggerConfig.level,
            options: {
              colorize: true,
              ignore: 'pid,hostname',
            },
          },
          // 文件：结构化 JSON（与生产环境格式一致，方便本地复现生产问题）
          // 相对路径基于进程 cwd 解析；mkdir 由 pino/file 自动创建目录
          {
            target: 'pino/file',
            level: loggerConfig.level,
            options: {
              destination: 'logs/app.log',
              mkdir: true,
            },
          },
        ],
      },
    })
    : pino({
      ...loggerConfig,
    });

// ==================== 便捷 Logger 工厂 ====================

/**
 * 轻量 Logger 接口，API 风格 (msg, data?)
 * 与 console.* 调用签名兼容，方便从 console.* 迁移
 * data 参数接受任意类型，非对象值会自动包装为 { value: data }
 */
export interface SimpleLogger {
  debug(msg: string, data?: any): void;
  info(msg: string, data?: any): void;
  warn(msg: string, data?: any): void;
  error(msg: string, data?: any): void;
  child(bindings: Record<string, unknown>): SimpleLogger;
}

/**
 * 创建带模块上下文的 SimpleLogger
 *
 * 用法：
 *   import { createLogger } from '@/lib/logger';
 *   const logger = createLogger('my-module');
 *   logger.info('操作完成', { detail: 'xxx' });
 *   logger.error('操作失败', { error: e.message });
 *
 * 底层委托给 baseLogger（Pino），自动继承
 * pino-pretty 格式化、日志级别控制等能力。
 */
export function createLogger(module: string): SimpleLogger {
  const child = baseLogger.child({ module });

  const wrapChild = (childLogger: Logger): SimpleLogger => ({
    debug: (msg, data) => {
      if (data !== undefined && data !== null) {
        const obj = (typeof data === 'object' && !Array.isArray(data)) ? data : { value: data };
        (childLogger as any).debug(obj, msg);
      } else {
        (childLogger as any).debug(msg);
      }
    },
    info: (msg, data) => {
      if (data !== undefined && data !== null) {
        const obj = (typeof data === 'object' && !Array.isArray(data)) ? data : { value: data };
        (childLogger as any).info(obj, msg);
      } else {
        (childLogger as any).info(msg);
      }
    },
    warn: (msg, data) => {
      if (data !== undefined && data !== null) {
        const obj = (typeof data === 'object' && !Array.isArray(data)) ? data : { value: data };
        (childLogger as any).warn(obj, msg);
      } else {
        (childLogger as any).warn(msg);
      }
    },
    error: (msg, data) => {
      if (data !== undefined && data !== null) {
        const obj = (typeof data === 'object' && !Array.isArray(data)) ? data : { value: data };
        (childLogger as any).error(obj, msg);
      } else {
        (childLogger as any).error(msg);
      }
    },
    child: (bindings: Record<string, unknown>) => wrapChild(childLogger.child(bindings)),
  });

  return wrapChild(child);
}
